import useTagOptions from '@bo/hooks/useTagOptions';
import useTags from '@bo/hooks/useTags';
import TagCard from '@boComponents/TagCard';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Grid from '@sharedComponents/Grid';
import Loader from '@sharedComponents/Loader';
import Pagination from '@sharedComponents/Pagination';
import SearchBar from '@sharedComponents/SearchBar';
import Spacer from '@sharedComponents/Spacer';
import Typography from '@sharedComponents/Typography';
import { useModal } from '@sharedContexts/ModalContext';
import { parseI18n } from '@src/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TagForm from './TagForm';

const TagList = () => {
    const { t } = useTranslation();
    const { actions: modalActions } = useModal();

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(16);
    const gridRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            if (!gridRef.current) return;
            setTimeout(() => {
                const { width } = gridRef.current.getBoundingClientRect();
                const itemsPerLine = Math.max(1, Math.floor(width / 270));
                setPerPage(itemsPerLine * 4);
            }, 0);
        };

        requestAnimationFrame(() => handleResize());

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined' && gridRef.current) {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(gridRef.current);
        }
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
        };
    }, []);

    const { tags, total, loading, refresh } = useTags({ search, page, perPage });
    const { languagesOptions, loading: optionsLoading } = useTagOptions();
    const totalPages = Math.ceil(total / perPage) || 1;

    const optionsRef = useRef(null);
    optionsRef.current = { languagesOptions, optionsLoading };

    const openCreateModal = useCallback(() => {
        const { languagesOptions: lao, optionsLoading: ol } = optionsRef.current;
        modalActions.open({
            component: TagForm,
            title: t('New tag'),
            size: 'm',
            languagesOptions: lao,
            optionsLoading: ol,
            onSuccess: () => {
                refresh();
                modalActions.close();
            },
        });
    }, [modalActions, refresh, t]);

    const openEditModal = useCallback(
        (tag) => {
            const { languagesOptions: lao, optionsLoading: ol } = optionsRef.current;
            const texts = parseI18n(tag.valeur);
            const parsedValeur = Object.keys(texts).length > 0 ? texts : { fr: tag.label };
            modalActions.open({
                component: TagForm,
                title: t('Edit tag'),
                size: 'm',
                defaultValues: {
                    type: tag.type,
                    valeur: parsedValeur,
                    motsCles: tag.motsCles ?? '',
                },
                tagId: tag.id,
                languagesOptions: lao,
                optionsLoading: ol,
                onSuccess: () => {
                    refresh();
                    modalActions.close();
                },
            });
        },
        [modalActions, refresh, t],
    );

    const handleSearch = useCallback(async (text) => {
        setSearch(text);
        setPage(1);
        return [];
    }, []);

    return (
        <FlexBox flexDirection="column" gap="m" padding="m">
            <FlexBox justifyContent="space-between" alignItems="center">
                <Typography fontWeight="bold" fontSize="xl">
                    {t('Tags')}
                </Typography>
                <Button label={t('New tag')} action={openCreateModal} />
            </FlexBox>

            <SearchBar
                search={handleSearch}
                minChars={1}
                placeholder={t('Search by label or keyword…')}
                onSelect={() => {}}
                onClear={() => {
                    setSearch('');
                    setPage(1);
                }}
                controlShouldRenderValue={false}
                hideMenu={true}
            />

            <Spacer y="s" />

            {loading && <Loader />}

            {!loading && tags.length === 0 && (
                <FlexBox justifyContent="center" alignItems="center" flexDirection="column" gap="s">
                    <Spacer y="m" />
                    <Typography>{t('No tags found.')}</Typography>
                </FlexBox>
            )}

            <div ref={gridRef}>
                {!loading && tags.length > 0 && (
                    <Grid>
                        {tags.map((tag) => (
                            <TagCard key={tag.id} {...tag} onClick={() => openEditModal(tag)} />
                        ))}
                    </Grid>
                )}
            </div>

            {totalPages > 1 && (
                <>
                    <Spacer y="s" />
                    <Pagination totalPages={totalPages} selectedPage={page} onChange={setPage} />
                </>
            )}
        </FlexBox>
    );
};

export default TagList;
